import { cropPixelImage } from '../image.js';
import { compileDecodeToken } from '../decoder.js';
import { composeGlyphSequenceWeights } from '../atlas.js';
import { getDefaultGlyphAtlas } from '../default-atlas.js';
import { createTextEvidence } from './client.js';
import { countAnchorBits, countColumnBits, firstInkX, popcountByte, popcountColumn, TEXT_COLUMN_MASK } from './mask.js';
import { assertTextEvidence } from './wire.js';
import { TEXT_EVIDENCE_HEIGHT, TEXT_MASK_WEIGHT_THRESHOLD, } from './types.js';
function uniqueCharset(text) {
    let out = '';
    const seen = new Set();
    for (const ch of text) {
        if (!seen.has(ch)) {
            seen.add(ch);
            out += ch;
        }
    }
    return out;
}
function clampByteThreshold(value) {
    const threshold = value ?? TEXT_MASK_WEIGHT_THRESHOLD;
    if (!Number.isInteger(threshold) || threshold < 1 || threshold > 255) {
        throw new RangeError('glyphWeightThreshold must be an integer in [1, 255]');
    }
    return threshold;
}
const compiledTextMaskCache = new Map();
const COMPILED_TEXT_MASK_CACHE_LIMIT = 4096;
/** Server helper: compile a claimed string into the exact bundled glyph shape.
 * Callers may cache the returned object for repeated known tokens. */
export function compileTextMask(text, options = {}) {
    const atlasVariant = options.atlasVariant ?? 'cleartype-rgb';
    const threshold = clampByteThreshold(options.glyphWeightThreshold);
    const cacheKey = options.atlas ? undefined : `${atlasVariant}\0${threshold}\0${text}`;
    const cached = cacheKey === undefined ? undefined : compiledTextMaskCache.get(cacheKey);
    if (cached)
        return cached;
    const atlas = options.atlas ?? getDefaultGlyphAtlas({ variant: atlasVariant, charset: uniqueCharset(text) });
    const token = compileDecodeToken(atlas, text);
    const logicalWidth = token.anchorKey.length;
    const anchorColumns = new Uint16Array(logicalWidth);
    for (let x = 0; x < logicalWidth; x++)
        anchorColumns[x] = token.anchorKey.charCodeAt(x) & 0x0fff;
    const composed = composeGlyphSequenceWeights(token.glyphs);
    if (composed.logicalWidth !== logicalWidth) {
        throw new Error(`compiled logical width ${composed.logicalWidth} differs from anchor width ${logicalWidth}`);
    }
    const visualWidth = composed.visualWidth;
    const inkColumns = new Uint16Array(visualWidth);
    for (let y = 0; y < TEXT_EVIDENCE_HEIGHT; y++) {
        for (let x = 0; x < visualWidth; x++) {
            const weight = (composed.weights[y * visualWidth + x] ?? 0) * 255;
            if (weight + 1e-9 >= threshold)
                inkColumns[x] = (inkColumns[x] ?? 0) | (1 << y);
        }
    }
    const compiled = {
        text,
        atlasVariant,
        width: logicalWidth,
        logicalWidth,
        visualWidth,
        height: TEXT_EVIDENCE_HEIGHT,
        anchorColumns,
        inkColumns,
        inkPixels: countColumnBits(inkColumns),
        glyphWeightThreshold: threshold,
    };
    if (cacheKey !== undefined) {
        if (compiledTextMaskCache.size >= COMPILED_TEXT_MASK_CACHE_LIMIT) {
            const oldest = compiledTextMaskCache.keys().next().value;
            if (oldest !== undefined)
                compiledTextMaskCache.delete(oldest);
        }
        compiledTextMaskCache.set(cacheKey, compiled);
    }
    return compiled;
}
function emptyResult(expectedText, reason) {
    return {
        matches: false,
        expectedText,
        reason,
        anchorMatched: false,
        anchorPixelMismatches: 0,
        extraAnchorPixels: 0,
        leftExtraAnchorPixels: 0,
        rightExtraAnchorPixels: 0,
        truePositivePixels: 0,
        falsePositivePixels: 0,
        falseNegativePixels: 0,
        extraInkPixels: 0,
        leftExtraInkPixels: 0,
        rightExtraInkPixels: 0,
        precision: 0,
        recall: 0,
        f1: 0,
        iou: 0,
    };
}
function integerOptionError(name, value, minimum = 0) {
    if (value === undefined)
        return undefined;
    return Number.isInteger(value) && value >= minimum ? undefined : `${name} must be an integer >= ${minimum}`;
}
function rateOptionError(name, value) {
    if (value === undefined)
        return undefined;
    return value >= 0 && value <= 1 ? undefined : `${name} must be in [0, 1]`;
}
function validationOptionsError(options) {
    return integerOptionError('expectedWidth', options.expectedWidth, 1)
        ?? integerOptionError('expectedCropHeight', options.expectedCropHeight, TEXT_EVIDENCE_HEIGHT)
        ?? integerOptionError('expectedLineY', options.expectedLineY)
        ?? integerOptionError('lineYTolerance', options.lineYTolerance)
        ?? integerOptionError('expectedStartX', options.expectedStartX)
        ?? integerOptionError('startXTolerance', options.startXTolerance)
        ?? integerOptionError('startSearchPad', options.startSearchPad)
        ?? integerOptionError('maxAnchorPixelMismatches', options.maxAnchorPixelMismatches)
        ?? integerOptionError('maxExtraAnchorPixels', options.maxExtraAnchorPixels)
        ?? integerOptionError('maxLeftExtraAnchorPixels', options.maxLeftExtraAnchorPixels)
        ?? integerOptionError('maxRightExtraAnchorPixels', options.maxRightExtraAnchorPixels)
        ?? integerOptionError('maxExtraInkPixels', options.maxExtraInkPixels)
        ?? integerOptionError('maxLeftExtraInkPixels', options.maxLeftExtraInkPixels)
        ?? integerOptionError('maxRightExtraInkPixels', options.maxRightExtraInkPixels)
        ?? integerOptionError('maxTextLength', options.maxTextLength, 1)
        ?? rateOptionError('minF1', options.minF1)
        ?? rateOptionError('maxFalsePositiveRate', options.maxFalsePositiveRate)
        ?? rateOptionError('maxFalseNegativeRate', options.maxFalseNegativeRate);
}
function geometryError(evidence, text, options) {
    if (options.expectedWidth !== undefined && evidence.width !== options.expectedWidth)
        return `crop width ${evidence.width} does not match expectedWidth ${options.expectedWidth}`;
    if (options.expectedCropHeight !== undefined && evidence.cropHeight !== options.expectedCropHeight)
        return `crop height ${evidence.cropHeight} does not match expectedCropHeight ${options.expectedCropHeight}`;
    if (options.expectedLineY !== undefined) {
        const tolerance = options.lineYTolerance ?? 0;
        if (Math.abs(evidence.lineY - options.expectedLineY) > tolerance)
            return `lineY ${evidence.lineY} is outside the expected range`;
    }
    const maxTextLength = options.maxTextLength ?? 128;
    if ([...text].length > maxTextLength)
        return `expected text exceeds maxTextLength ${maxTextLength}`;
    return undefined;
}
function anchorMatchesAt(evidence, compiled, startX) {
    if (startX < 0 || startX + compiled.logicalWidth > evidence.width)
        return false;
    for (let x = 0; x < compiled.logicalWidth; x++) {
        if ((evidence.anchorColumns[startX + x] ?? 0) !== (compiled.anchorColumns[x] ?? 0))
            return false;
    }
    return true;
}
function candidateStarts(evidence, compiled, options) {
    const maxStart = evidence.width - compiled.visualWidth;
    if (maxStart < 0)
        return [];
    if (options.expectedStartX !== undefined) {
        const tolerance = options.startXTolerance ?? 0;
        const out = [];
        for (let x = Math.max(0, options.expectedStartX - tolerance); x <= Math.min(maxStart, options.expectedStartX + tolerance); x++)
            out.push(x);
        return out;
    }
    const exact = [];
    for (let x = 0; x <= maxStart; x++) {
        if (anchorMatchesAt(evidence, compiled, x))
            exact.push(x);
    }
    if (exact.length > 0)
        return exact;
    const observedStart = firstInkX(evidence.anchorColumns, evidence.inkColumns) ?? 0;
    const pad = options.startSearchPad ?? 2;
    const fallback = [];
    for (let x = Math.max(0, observedStart - pad); x <= Math.min(maxStart, observedStart + pad); x++)
        fallback.push(x);
    return fallback;
}
function validateAt(evidence, compiled, startX, options) {
    let anchorPixelMismatches = 0;
    for (let x = 0; x < compiled.logicalWidth; x++) {
        const difference = (evidence.anchorColumns[startX + x] ?? 0) ^ (compiled.anchorColumns[x] ?? 0);
        anchorPixelMismatches += popcountByte(difference) + popcountByte(difference >>> 8);
    }
    const leftExtraAnchorPixels = countAnchorBits(evidence.anchorColumns, 0, startX);
    const rightExtraAnchorPixels = countAnchorBits(evidence.anchorColumns, startX + compiled.logicalWidth, evidence.width);
    const extraAnchorPixels = leftExtraAnchorPixels + rightExtraAnchorPixels;
    let truePositivePixels = 0;
    let falsePositivePixels = 0;
    let falseNegativePixels = 0;
    let extraInkPixels = 0;
    let leftExtraInkPixels = 0;
    let rightExtraInkPixels = 0;
    for (let x = 0; x < evidence.width; x++) {
        const observed = (evidence.inkColumns[x] ?? 0) & TEXT_COLUMN_MASK;
        const relativeX = x - startX;
        const within = relativeX >= 0 && relativeX < compiled.visualWidth;
        const expected = within ? (compiled.inkColumns[relativeX] ?? 0) & TEXT_COLUMN_MASK : 0;
        truePositivePixels += popcountColumn(observed & expected);
        const falsePositiveColumn = observed & ~expected & TEXT_COLUMN_MASK;
        const falsePositiveCount = popcountColumn(falsePositiveColumn);
        falsePositivePixels += falsePositiveCount;
        falseNegativePixels += popcountColumn(expected & ~observed & TEXT_COLUMN_MASK);
        if (!within && falsePositiveCount > 0) {
            extraInkPixels += falsePositiveCount;
            if (x < startX)
                leftExtraInkPixels += falsePositiveCount;
            else
                rightExtraInkPixels += falsePositiveCount;
        }
    }
    const scoredFalsePositivePixels = options.ignoreExtraInkInShapeScore ? falsePositivePixels - extraInkPixels : falsePositivePixels;
    const precisionDenominator = truePositivePixels + scoredFalsePositivePixels;
    const recallDenominator = truePositivePixels + falseNegativePixels;
    const union = truePositivePixels + scoredFalsePositivePixels + falseNegativePixels;
    const precision = precisionDenominator === 0 ? (compiled.inkPixels === 0 ? 1 : 0) : truePositivePixels / precisionDenominator;
    const recall = recallDenominator === 0 ? 1 : truePositivePixels / recallDenominator;
    const f1 = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall);
    const iou = union === 0 ? 1 : truePositivePixels / union;
    const falsePositiveRate = precisionDenominator === 0 ? 0 : scoredFalsePositivePixels / precisionDenominator;
    const falseNegativeRate = recallDenominator === 0 ? 0 : falseNegativePixels / recallDenominator;
    const requireExactAnchors = options.requireExactAnchors ?? true;
    const maxAnchorPixelMismatches = options.maxAnchorPixelMismatches ?? 0;
    const maxExtraAnchorPixels = options.maxExtraAnchorPixels ?? 0;
    const maxLeftExtraAnchorPixels = options.maxLeftExtraAnchorPixels ?? maxExtraAnchorPixels;
    const maxRightExtraAnchorPixels = options.maxRightExtraAnchorPixels ?? maxExtraAnchorPixels;
    const minF1 = options.minF1 ?? 1.00;
    const maxFalsePositiveRate = options.maxFalsePositiveRate ?? 0;
    const maxFalseNegativeRate = options.maxFalseNegativeRate ?? 0;
    const maxExtraInkPixels = options.maxExtraInkPixels ?? 0;
    const maxLeftExtraInkPixels = options.maxLeftExtraInkPixels ?? maxExtraInkPixels;
    const maxRightExtraInkPixels = options.maxRightExtraInkPixels ?? maxExtraInkPixels;
    const anchorMatched = anchorPixelMismatches <= maxAnchorPixelMismatches
        && leftExtraAnchorPixels <= maxLeftExtraAnchorPixels
        && rightExtraAnchorPixels <= maxRightExtraAnchorPixels;
    const matches = (!requireExactAnchors || anchorMatched)
        && f1 >= minF1
        && falsePositiveRate <= maxFalsePositiveRate
        && falseNegativeRate <= maxFalseNegativeRate
        && leftExtraInkPixels <= maxLeftExtraInkPixels
        && rightExtraInkPixels <= maxRightExtraInkPixels;
    let reason;
    if (requireExactAnchors && anchorPixelMismatches > maxAnchorPixelMismatches)
        reason = `anchor pixels differ by ${anchorPixelMismatches}`;
    else if (requireExactAnchors && leftExtraAnchorPixels > maxLeftExtraAnchorPixels)
        reason = `found ${leftExtraAnchorPixels} extra anchor pixels left of the expected text`;
    else if (requireExactAnchors && rightExtraAnchorPixels > maxRightExtraAnchorPixels)
        reason = `found ${rightExtraAnchorPixels} extra anchor pixels right of the expected text`;
    else if (leftExtraInkPixels > maxLeftExtraInkPixels)
        reason = `found ${leftExtraInkPixels} extra ink pixels left of the expected text`;
    else if (rightExtraInkPixels > maxRightExtraInkPixels)
        reason = `found ${rightExtraInkPixels} extra ink pixels right of the expected text`;
    else if (f1 < minF1)
        reason = `shape F1 ${f1.toFixed(3)} is below ${minF1.toFixed(3)}`;
    else if (falsePositiveRate > maxFalsePositiveRate)
        reason = `false-positive rate ${falsePositiveRate.toFixed(3)} is too high`;
    else if (falseNegativeRate > maxFalseNegativeRate)
        reason = `false-negative rate ${falseNegativeRate.toFixed(3)} is too high`;
    return {
        matches,
        expectedText: compiled.text,
        ...(reason ? { reason } : {}),
        atlasVariant: compiled.atlasVariant,
        startX,
        endX: startX + compiled.logicalWidth,
        visualEndX: startX + compiled.visualWidth,
        anchorMatched,
        anchorPixelMismatches,
        extraAnchorPixels,
        leftExtraAnchorPixels,
        rightExtraAnchorPixels,
        truePositivePixels,
        falsePositivePixels,
        falseNegativePixels,
        extraInkPixels,
        leftExtraInkPixels,
        rightExtraInkPixels,
        precision,
        recall,
        f1,
        iou,
    };
}
function better(a, b) {
    if (!a)
        return b;
    if (a.matches !== b.matches)
        return b.matches ? b : a;
    if (a.anchorMatched !== b.anchorMatched)
        return b.anchorMatched ? b : a;
    if (a.anchorPixelMismatches !== b.anchorPixelMismatches)
        return b.anchorPixelMismatches < a.anchorPixelMismatches ? b : a;
    if (a.extraAnchorPixels !== b.extraAnchorPixels)
        return b.extraAnchorPixels < a.extraAnchorPixels ? b : a;
    if (a.extraInkPixels !== b.extraInkPixels)
        return b.extraInkPixels < a.extraInkPixels ? b : a;
    return b.f1 > a.f1 ? b : a;
}
/** Validate a client-produced compact mask against a claimed text value. */
export function validateTextEvidence(evidence, expectedText, options = {}) {
    try {
        assertTextEvidence(evidence, options);
    }
    catch (error) {
        return emptyResult(expectedText, error instanceof Error ? error.message : 'invalid evidence');
    }
    const optionError = validationOptionsError(options);
    if (optionError)
        return emptyResult(expectedText, optionError);
    const geometry = geometryError(evidence, expectedText, options);
    if (geometry)
        return emptyResult(expectedText, geometry);
    if (expectedText.length === 0) {
        const anchors = countAnchorBits(evidence.anchorColumns);
        const ink = countColumnBits(evidence.inkColumns);
        const matches = anchors === 0 && ink === 0;
        return {
            matches,
            expectedText,
            ...(matches ? {} : { reason: 'expected empty text but evidence contains ink' }),
            anchorMatched: anchors === 0,
            anchorPixelMismatches: 0,
            extraAnchorPixels: anchors,
            leftExtraAnchorPixels: anchors,
            rightExtraAnchorPixels: 0,
            truePositivePixels: 0,
            falsePositivePixels: ink,
            falseNegativePixels: 0,
            extraInkPixels: ink,
            leftExtraInkPixels: ink,
            rightExtraInkPixels: 0,
            precision: matches ? 1 : 0,
            recall: 1,
            f1: matches ? 1 : 0,
            iou: matches ? 1 : 0,
        };
    }
    const variants = options.atlas
        ? [options.atlasVariant === 'cleartype-bgr' ? 'cleartype-bgr' : 'cleartype-rgb']
        : options.atlasVariant === 'cleartype-rgb' || options.atlasVariant === 'cleartype-bgr'
            ? [options.atlasVariant]
            : ['cleartype-rgb', 'cleartype-bgr'];
    let best;
    for (const atlasVariant of variants) {
        let compiled;
        try {
            compiled = compileTextMask(expectedText, {
                atlas: options.atlas,
                atlasVariant,
                glyphWeightThreshold: options.glyphWeightThreshold,
            });
        }
        catch (error) {
            best = better(best, emptyResult(expectedText, error instanceof Error ? error.message : 'could not compile expected text'));
            continue;
        }
        if (compiled.visualWidth > evidence.width) {
            best = better(best, emptyResult(expectedText, `expected visual text width ${compiled.visualWidth} exceeds crop width ${evidence.width}`));
            continue;
        }
        const starts = candidateStarts(evidence, compiled, options);
        if (starts.length === 0) {
            best = better(best, emptyResult(expectedText, 'no valid horizontal start position'));
            continue;
        }
        for (const startX of starts)
            best = better(best, validateAt(evidence, compiled, startX, options));
    }
    return best ?? emptyResult(expectedText, 'no atlas variant could validate the text');
}
/** Validate directly from a decoded image crop. Useful when the server receives
 * PNG pixels rather than the compact client evidence format. */
export function validateTextImageStrict(image, expectedText, options) {
    try {
        const evidence = createTextEvidence(image, options);
        return validateTextEvidence(evidence, expectedText, options);
    }
    catch (error) {
        return emptyResult(expectedText, error instanceof Error ? error.message : 'could not extract evidence');
    }
}
export function validateTextCropStrict(source, rect, expectedText, options) {
    try {
        return validateTextImageStrict(cropPixelImage(source, rect), expectedText, options);
    }
    catch (error) {
        return emptyResult(expectedText, error instanceof Error ? error.message : 'could not crop validation image');
    }
}
//# sourceMappingURL=server.js.map